import { addDays } from 'date-fns';

// Action types
export const CREDIT_NOTE_ACTIONS = {
    UPDATE_CREDIT_NOTE_DATA: 'UPDATE_CREDIT_NOTE_DATA',
    SET_CUSTOMER: 'SET_CUSTOMER',
    SET_ITEMS: 'SET_ITEMS',
    UPDATE_ITEM: 'UPDATE_ITEM',
    ADD_ITEM: 'ADD_ITEM',
    REMOVE_ITEM: 'REMOVE_ITEM',
    UPDATE_DELIVERY_OPTIONS: 'UPDATE_DELIVERY_OPTIONS',
    TOGGLE_PAYMENT_METHOD: 'TOGGLE_PAYMENT_METHOD',
    SET_TEMPLATE: 'SET_TEMPLATE',
    LOAD_CREDIT_NOTE: 'LOAD_CREDIT_NOTE',
    LOAD_ORIGINAL_INVOICE: 'LOAD_ORIGINAL_INVOICE',
    RESET: 'RESET'
};

// Initial state factory
export const createInitialCreditNoteState = (originalInvoiceId = null, isEditMode = false, invoiceSettings = {}) => {
    // Extract payment methods that are enabled
    const enabledPaymentMethods = {};
    if (invoiceSettings?.paymentMethods) {
        const methods = ['bankgiro', 'plusgiro', 'paypal', 'swish'];
        methods.forEach(method => {
            enabledPaymentMethods[method] = invoiceSettings.paymentMethods[method] === true;
        });
    }

    // Get default delivery method
    const defaultDeliveryMethod = invoiceSettings?.deliveryChannels?.defaultChannel || 'download';

    // Get default VAT rate
    const defaultVatRate = invoiceSettings?.taxSettings?.defaultVatRate || 25;

    // Get default payment terms
    const paymentDays = invoiceSettings?.defaultTerms?.paymentDays || 30;

    const template = invoiceSettings?.template?.defaultTemplate || 'classic';

    return {
        // Customer info
        customerid: '',
        customer: null,

        // Credit Note details
        creditNoteNumber: isEditMode ? '' : 'NY KREDITFAKTURA',
        creditNoteDate: new Date(),
        dueDate: addDays(new Date(), paymentDays),

        // Original invoice reference
        originalInvoiceId: originalInvoiceId,
        originalInvoice: null,

        // Financial settings
        currency: 'SEK',
        roundTotal: false,
        defaultVatRate: defaultVatRate,

        // Content
        items: [],
        notes: '',
        attachments: [],

        // Status
        status: 'Draft',

        // Delivery options - initialized from settings
        deliveryOptions: {
            method: defaultDeliveryMethod,
            email: '',
            phone: '',
            message: 'Här är din kreditfaktura enligt överenskommelse.',
            subject: 'Kreditfaktura från ditt företag'
        },

        // Payment methods - initialized from settings
        paymentMethods: enabledPaymentMethods,

        // Template - initialized from settings
        selectedTemplate: template,
    };
};

// Credit Note reducer
export const creditNoteReducer = (state, action) => {
    switch (action.type) {
        case CREDIT_NOTE_ACTIONS.UPDATE_CREDIT_NOTE_DATA:
            return {
                ...state,
                ...action.payload
            };

        case CREDIT_NOTE_ACTIONS.SET_CUSTOMER:
            return {
                ...state,
                customer: action.payload,
                customerid: action.payload?.id || '',
                // Update delivery email if customer has email
                deliveryOptions: {
                    ...state.deliveryOptions,
                    email: action.payload?.email || state.deliveryOptions.email
                }
            };

        case CREDIT_NOTE_ACTIONS.SET_ITEMS:
            return {
                ...state,
                items: action.payload
            };

        case CREDIT_NOTE_ACTIONS.UPDATE_ITEM:
            return {
                ...state,
                items: state.items.map((item, idx) =>
                    idx === action.index ? action.payload : item
                )
            };

        case CREDIT_NOTE_ACTIONS.ADD_ITEM:
            return {
                ...state,
                items: [...state.items, action.payload]
            };

        case CREDIT_NOTE_ACTIONS.REMOVE_ITEM:
            return {
                ...state,
                items: state.items.filter((_, idx) => idx !== action.index)
            };

        case CREDIT_NOTE_ACTIONS.UPDATE_DELIVERY_OPTIONS:
            return {
                ...state,
                deliveryOptions: {
                    ...state.deliveryOptions,
                    ...action.payload
                }
            };

        case CREDIT_NOTE_ACTIONS.TOGGLE_PAYMENT_METHOD:
            return {
                ...state,
                paymentMethods: {
                    ...state.paymentMethods,
                    [action.payload]: !state.paymentMethods[action.payload]
                }
            };

        case CREDIT_NOTE_ACTIONS.SET_TEMPLATE:
            return {
                ...state,
                selectedTemplate: action.payload
            };

        case CREDIT_NOTE_ACTIONS.LOAD_ORIGINAL_INVOICE:
            // When loading original invoice for credit note creation
            const originalInvoice = action.payload;

            // Parse dates properly from API response
            const parseApiDate = (dateString) => {
                if (!dateString) return new Date();
                try {
                    return new Date(dateString);
                } catch (e) {
                    return new Date();
                }
            };

            // Map customer data from flat API structure - THIS IS THE FIX
            const customerData = {
                id: originalInvoice.customerid,
                name: originalInvoice.customername,
                email: originalInvoice.email,
                phone: originalInvoice.phone,
                orgnumber: originalInvoice.orgnumber,
                type: originalInvoice.orgnumber ? 'company' : 'individual',
                number: originalInvoice.kundnummer,
                // Add other customer fields from your API response
                street1: originalInvoice.street1,
                city: originalInvoice.city,
                postalcode: originalInvoice.postalcode,
                country: originalInvoice.country
            };

            return {
                ...state,
                originalInvoice: originalInvoice,
                customer: customerData, // Use the properly mapped customer data
                customerid: originalInvoice.customerid || '',
                currency: originalInvoice.currency || 'SEK',

                // Properly map items with stable IDs and correct data types
                items: originalInvoice.items ? originalInvoice.items.map(item => ({
                    id: item.item_id || `item-${Math.random()}`,
                    productcode: item.productcode || '',
                    description: item.description || '',
                    unitprice: parseFloat(item.unitprice) || 0,
                    quantity: 0, // Start with 0 for credit notes
                    original_quantity: parseInt(item.quantity) || 0,
                    unit: item.unit || 'st',
                    tax_rate: item.tax_rate || '25%',
                    type: item.type || 'Product',
                    discount: 0
                })) : [],

                notes: `Kreditfaktura för faktura #${originalInvoice.invoiceNumber || originalInvoice.invoicenumber}`,

                deliveryOptions: {
                    ...state.deliveryOptions,
                    email: customerData.email || ''
                }
            };

        case CREDIT_NOTE_ACTIONS.LOAD_CREDIT_NOTE:
            // When loading existing credit note for editing
            const loadedCreditNote = action.payload;
            return {
                ...state,
                customerid: loadedCreditNote.customerid || '',
                customer: loadedCreditNote.customer || null,
                creditNoteNumber: loadedCreditNote.creditNoteNumber || loadedCreditNote.invoicenumber || '',
                creditNoteDate: loadedCreditNote.creditNoteDate || loadedCreditNote.invoicedate || new Date(),
                dueDate: loadedCreditNote.dueDate || loadedCreditNote.deadline || addDays(new Date(), 30),
                currency: loadedCreditNote.currency || 'SEK',
                roundTotal: loadedCreditNote.roundTotal || !loadedCreditNote.rounding || false,
                items: loadedCreditNote.items || [],
                notes: loadedCreditNote.notes || '',
                attachments: loadedCreditNote.attachments || [],
                status: loadedCreditNote.status || 'Draft',
                deliveryOptions: {
                    ...state.deliveryOptions,
                    ...(loadedCreditNote.deliveryOptions || {})
                },
                paymentMethods: loadedCreditNote.paymentMethods ? {
                    ...state.paymentMethods,
                    ...loadedCreditNote.paymentMethods
                } : state.paymentMethods,
                selectedTemplate: loadedCreditNote.selectedTemplate || state.selectedTemplate,
                originalInvoiceId: loadedCreditNote.related_invoice_id || loadedCreditNote.originalInvoiceId
            };

        case CREDIT_NOTE_ACTIONS.RESET:
            return action.payload || createInitialCreditNoteState();

        default:
            return state;
    }
};